import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { track } from '@lf/analytics-events';
import lfApi from '@lf/api-client/lfApi';
import { LoadingButton } from '@lf/ui-primitives';
import { readPendingShareToken } from '../lib/share/pendingShare';
import { useUser } from '../contexts/UserContext';

function LoginPage() {
    const { fetchUser } = useUser();
    const emailLogin = 'email';
    const tokenLogin = 'token';

    const [parentCred, setParentCred] = useState(
        { type: 'user', username: '', token: '' });
    const [loginType, setLoginType] = useState(emailLogin);
    const [isParentLoggingIn, setIsParentLoggingIn] = useState(false);
    const [loginError, setLoginError] = useState('');
    const [childLinkToken, setChildLinkToken] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const username = params.get('username');
        if (username) {
            setParentCred(prevState => ({ ...prevState, username }));
            setLoginType(tokenLogin);
        }
        const childLink = params.get('child_link');
        if (childLink) {
            setChildLinkToken(childLink);
        }
        // Single-step parent signup/login form — fire signup_started
        // on initial mount so the funnel reports show drop-off between
        // landing and code-entry. The backend disambiguates whether
        // the email_token flow created a new account (signup_completed)
        // or just authenticated an existing one.
        track('signup_started', { step: 'email', app_role: 'parent' });
    }, []);

    async function _handleLogin(event, loginType) {
        if (event)
            event.preventDefault();

        if (loginType === emailLogin && !parentCred.username) {
            return false;
        }
        if (loginType === tokenLogin && !parentCred.token) {
            return false;
        }

        setIsParentLoggingIn(true);
        setLoginError('');

        try {
            const loginUrl = loginType === emailLogin ? '/auth/login_cred' : '/auth/login_token';
            const response = await lfApi.post(loginUrl, parentCred);
            if (response.status === 200) {
                if (response.data.success) {
                    if (loginType === emailLogin) {
                        // Email step succeeded — verification email is on
                        // the way. The backend fires email_sent(kind=verification);
                        // the frontend doesn't fire login here because the user
                        // has not yet authenticated. They click the link or enter
                        // the token, which triggers the token step below.
                        setLoginType(tokenLogin);
                    } else {
                        track('login', { method: 'email_token' });
                        await fetchUser();
                        if (childLinkToken) {
                            navigate(`/child-link/${childLinkToken}`);
                        } else {
                            const shareToken = readPendingShareToken();
                            if (shareToken) {
                                navigate(`/share/${shareToken}`);
                            }
                        }
                    }
                } else {
                    setLoginError(loginType === emailLogin
                        ? 'Unable to send verification code. Please try again.'
                        : 'Invalid code, please try again.');
                    track('form_submission_failed', {
                        form: 'login',
                        field: loginType === emailLogin ? 'username' : 'token',
                        reason: loginType === emailLogin ? 'send_failed' : 'invalid_token',
                    });
                }
            } else {
                console.log('Login failed');
                setLoginError('Login failed. Please try again later.');
                track('form_submission_failed', {
                    form: 'login',
                    field: 'submit',
                    reason: 'http_error',
                });
            }
        }
        catch (error) {
            console.error('Login failed: ', error);
            setLoginError('An error occurred. Please try again later.');
            track('form_submission_failed', {
                form: 'login',
                field: 'submit',
                reason: 'exception',
            });
        }

        setIsParentLoggingIn(false);
        return true;
    };

    async function _handleEmailLogin(event) {
        const result = await _handleLogin(event, emailLogin);
        if (result === false && loginType === tokenLogin) {
            setLoginType(emailLogin);
            setLoginError('');
        }
        setParentCred(prevState => ({ ...prevState, token: '' }));
    }
    
    async function handleParentLogin(event) {
        return await _handleLogin(event, loginType);
    }

    function handleParentChange(e) {
        const { name, value } = e.target;
        setParentCred(prevState => ({ ...prevState, [name]: value }));
    };

    return <div className="flex mx-auto max-w-screen-lg my-8">
        <div className="inline-flex flex-col items-center border border-gray-300 bg-white rounded-2xl mx-auto py-6 px-6 sm:px-20">
            <div className="text-sm mb-1">Welcome to Learnforest</div>
            <h2 className="text-lg mb-6">Parent Login</h2>
            <form onSubmit={handleParentLogin} className="flex flex-col items-center">
                {
                    loginType === emailLogin && <input
                        type="email"
                        className="border border-gray-300 p-1 my-1"
                        name="username"
                        placeholder="Email"
                        value={parentCred.username}
                        onChange={handleParentChange} />
                }
                {
                    loginType === tokenLogin && <div className="flex flex-col text-sm mb-5 gap-2 sm:gap-0">
                        <div>Verification code sent to {parentCred.username}.</div>
                        <div>Check the spam folder if you haven't received it, 
                            or <span className="text-blue-500 underline cursor-pointer" onClick={_handleEmailLogin}>resend</span>.
                        </div>
                    </div>
                }
                {
                    loginType === tokenLogin && <input
                        type="text"
                        className="border border-gray-300 p-1 my-1"
                        name="token"
                        pattern="\d{6}" 
                        maxLength="6" 
                        inputMode="numeric"
                        placeholder="Verification code"
                        value={parentCred.token}
                        onChange={handleParentChange} />
                }
                
                {loginError && <div className="text-red-500 text-sm mt-2">{loginError}</div>}
                
                <LoadingButton
                    type="submit"
                    className="bg-blue-500 text-white py-2 px-4 rounded my-3"
                    text="Login"
                    isLoading={isParentLoggingIn}
                    onClick={handleParentLogin} />

                {
                    loginType === emailLogin && <div className="text-sm mt-6 text-center">
                        New here? Enter your email and we&apos;ll create your parent account automatically.
                    </div>
                }
            </form>
        </div>
    </div>;
}

export default LoginPage;
